import { supabase } from '../supabase/client';
import { getBeijingToday } from './timeUtils';
import { categoryStore } from '../store/categoryStore';

export interface PermissionCheckResult {
  hasPermission: boolean;
  expiredAt?: string;
  message: string;
}

export interface UserPermission {
  id: string;
  user_id: string;
  organization: string;
  category: string;
  level_text: string;
  expire_date: string;
  is_active: boolean;
}

/** 全站通套餐标识值：user_packages 三列（organization/category/level_text）均写此值 */
export const SITE_WIDE = '全站通';

/** 判断一条 user_packages 记录是否为全站通套餐（全站所有试卷解锁，不受三级限制） */
export function isSiteWidePackage(pkg: { organization?: string | null } | null | undefined): boolean {
  return !!pkg && pkg.organization === SITE_WIDE;
}

/**
 * 分类匹配：兼容 code（如 "scratch"）和 name（如 "图形化编程"）两种值
 * papers.category_type 字段历史数据存在两种格式，user_packages.category 同样存在两种格式
 */
export function isCategoryMatch(pkgCategory: string, categoryType: string): boolean {
  if (!categoryType) return true;
  if (pkgCategory === categoryType) return true;
  // 双向转换：传入的 code 转 name，传入的 name 转 code，再比较
  const codeToName = categoryStore.getCategoryNameByCode(categoryType);
  const nameToCode = categoryStore.getCategoryCodeByName(categoryType);
  if (codeToName && codeToName !== categoryType && pkgCategory === codeToName) return true;
  if (nameToCode && nameToCode !== categoryType && pkgCategory === nameToCode) return true;
  return false;
}

export async function checkExamPermission(
  userId: string,
  organizationCode: string,
  categoryType: string,
  level: number | string
): Promise<PermissionCheckResult> {
  // 防御：userId 缺失（未登录/会话未就绪/调用方竞态）时不发起查询，
  // 避免 user_id=eq.undefined 触发 PostgREST 400（uuid 列拒绝 "undefined" 字符串）
  if (!userId) {
    return { hasPermission: false, message: '请先登录' };
  }
  try {
    const today = getBeijingToday();
    const levelStr = level?.toString() || '';

    // 统一查询 user_packages 表（教师和学员套餐都在此表中）
    const { data: userPackages, error: userError } = await supabase
      .from('user_packages')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .gte('expire_date', today);

    if (userError) {
      throw userError;
    }

    // 全站通套餐：优先命中，解锁全站所有试卷（不受课程体系/分类/级别限制）
    const siteWidePackages = (userPackages || []).filter(isSiteWidePackage);
    if (siteWidePackages.length > 0) {
      const earliestExpiry = siteWidePackages
        .sort((a, b) => new Date(a.expire_date).getTime() - new Date(b.expire_date).getTime())[0];
      return {
        hasPermission: true,
        expiredAt: earliestExpiry.expire_date,
        message: '权限验证通过（全站通）',
      };
    }

    const matchedPackages = (userPackages || []).filter(pkg => {
      if (isSiteWidePackage(pkg)) return false;
      const orgMatch = pkg.organization === organizationCode;
      const catMatch = isCategoryMatch(pkg.category, categoryType);
      const levelMatch = pkg.level_text === levelStr;
      return orgMatch && catMatch && levelMatch;
    });

    if (matchedPackages.length > 0) {
      const earliestExpiry = matchedPackages
        .sort((a, b) => new Date(a.expire_date).getTime() - new Date(b.expire_date).getTime())[0];

      const source = earliestExpiry.package_source || 'student_purchase';
      let sourceText = '权限验证通过';
      if (source === 'admin_grant') sourceText = '权限验证通过（后台开通）';
      if (source === 'teacher_purchase') sourceText = '权限验证通过（教师自购）';

      return {
        hasPermission: true,
        expiredAt: earliestExpiry.expire_date,
        message: sourceText,
      };
    }

    return {
      hasPermission: false,
      message: '您没有该刷题包的访问权限，请先购买套餐',
    };
  } catch (error) {
    return {
      hasPermission: false,
      message: '权限检查失败，请稍后重试',
    };
  }
}

export async function getUserPermissions(userId: string): Promise<UserPermission[]> {
  // 防御：userId 缺失时返回空列表，避免 user_id=eq.undefined 非法查询（PostgREST 400）
  if (!userId) return [];
  try {
    const today = getBeijingToday();

    const { data: userPackages, error: userError } = await supabase
      .from('user_packages')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .gte('expire_date', today);

    if (userError) throw userError;

    const allPermissions: UserPermission[] = [];

    (userPackages || []).forEach(p => {
      allPermissions.push({
        id: p.id,
        user_id: p.user_id,
        organization: p.organization,
        category: p.category,
        level_text: p.level_text,
        expire_date: p.expire_date,
        is_active: p.is_active,
      });
    });

    return allPermissions;
  } catch {
    return [];
  }
}

export function getRemainingDays(expiredAt: string): number {
  const now = new Date();
  const expire = new Date(expiredAt);
  const diff = expire.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function formatRemainingTime(expiredAt: string): string {
  const days = getRemainingDays(expiredAt);
  if (days < 0) return '已过期';
  if (days === 0) return '今天到期';
  if (days === 1) return '明天到期';
  if (days <= 7) return `${days}天后到期`;
  if (days <= 30) return `${Math.floor(days / 7)}周后到期`;
  return `${Math.floor(days / 30)}个月后到期`;
}
